/**
 * The deck's drifting motes (ADR 0033: the 180-mote `Points` system stays at
 * Medium, as the town's points did, ADR 0028 decision 3; lifted from
 * brad-airship `airship-weather.js` `createMotes` as committed at 8fd5a09:
 * the 28 September GPU pass removed it from the prototype to fit its 8 ms
 * gate, and slice 7a keeps it). One draw of specks round the deck, animated
 * in the vertex shader: sun-caught dust by day, embers at dusk, cold
 * sparkles at night (the time of day's `motes` colour, alpha and radius). They
 * drift downwind, stream astern in flight and go out of focus with the lens.
 * Off draws none (the view hides them and frees their program).
 */
import * as THREE from 'three';

export type MoteLook = { readonly color: THREE.Color; readonly alpha: number; readonly radius: number };

export type Motes = {
  readonly points: THREE.Points;
  readonly count: number;
  update(dt: number, options: { readonly time: number; readonly driftX: number; readonly driftZ: number; readonly look: MoteLook; readonly viewportHeight: number; readonly camera: THREE.PerspectiveCamera; readonly visible: boolean }): void;
  dispose(): void;
};

/** `count` motes in the box from `min` (three's axes) over `size` metres. */
export const createMotes = (count: number, min: readonly [number, number, number], size: readonly [number, number, number]): Motes => {
  const seeds = new Float32Array(count * 4);
  let s = 12345;
  const rnd = (): number => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
  for (let i = 0; i < count; i += 1) seeds.set([rnd(), rnd(), rnd(), rnd()], i * 4);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(count * 3), 3));
  geometry.setAttribute('seed', new THREE.Float32BufferAttribute(seeds, 4));
  const uniforms = {
    uTime: { value: 0 },
    uDrift: { value: new THREE.Vector3() },
    uMin: { value: new THREE.Vector3(...min) },
    uSize: { value: new THREE.Vector3(...size) },
    uColor: { value: new THREE.Color('#fff0c8') },
    uAlpha: { value: 0.5 },
    uRadius: { value: 0.05 },
    uPx: { value: 500 },
  };
  const material = new THREE.ShaderMaterial({
    uniforms,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: `attribute vec4 seed;uniform float uTime,uRadius,uPx;uniform vec3 uDrift,uMin,uSize;varying float vA;
      void main(){
        vec3 p=seed.xyz*uSize+uDrift*(.7+.6*seed.w);
        p+=vec3(sin(uTime*.4+seed.w*40.),sin(uTime*.31+seed.x*30.)*.6,cos(uTime*.37+seed.y*50.))*.5;
        p=uMin+mod(p,uSize);
        vec4 mv=modelViewMatrix*vec4(p,1.);
        gl_Position=projectionMatrix*mv;
        float tw=.5+.5*sin(uTime*(1.3+seed.w*2.4)+seed.z*60.);
        vA=tw*smoothstep(0.,.08,min(min(p.x-uMin.x,uMin.x+uSize.x-p.x),min(p.z-uMin.z,uMin.z+uSize.z-p.z))/uSize.x*4.);
        gl_PointSize=max(1.5,uPx*uRadius*(.6+.8*seed.w)/-mv.z);
      }`,
    fragmentShader: `uniform vec3 uColor;uniform float uAlpha;varying float vA;
      void main(){vec2 c=gl_PointCoord-.5;float d=dot(c,c)*4.;float a=exp(-d*3.)*(1.-d);if(a<=0.)discard;gl_FragColor=vec4(uColor*a*uAlpha*vA*2.2,1.);}`,
  });
  material.name = 'airship-motes';
  const points = new THREE.Points(geometry, material);
  points.name = 'Weather motes';
  points.frustumCulled = false;
  points.renderOrder = 5;
  return {
    points,
    count,
    update(dt, { time, driftX, driftZ, look, viewportHeight, camera, visible }) {
      points.visible = visible && look.alpha > 0.01;
      if (!points.visible) return;
      uniforms.uTime.value = time;
      uniforms.uDrift.value.x += driftX * dt;
      uniforms.uDrift.value.y += Math.sin(time * 0.2) * 0.05 * dt;
      uniforms.uDrift.value.z += driftZ * dt;
      uniforms.uColor.value.copy(look.color);
      uniforms.uAlpha.value = look.alpha;
      uniforms.uRadius.value = look.radius;
      uniforms.uPx.value = (camera.projectionMatrix.elements[5] ?? 1) * viewportHeight * 0.5;
    },
    dispose() {
      geometry.dispose();
      material.dispose();
    },
  };
};
